/**
 * EquipmentPanel - UI panel for displaying equipped armor/accessories
 */

import { UIPanel } from './UIPanel';
import { Vector2 } from '../math/Vector2';
import { EquipmentComponent } from '../items/EquipmentComponent';
import { EquipmentSlot, ArmorItem } from '../items/Item';
import { InventoryComponent } from '../items/InventoryComponent';
import { Localization, t } from '../systems/Localization';
import { renderItemIcon, hasItemIcon } from './ItemIcons';

interface SlotConfig {
  slot: EquipmentSlot;
  label: string;
  x: number;
  y: number;
}

export class EquipmentPanel extends UIPanel {
  private equipment: EquipmentComponent | null = null;
  private inventory: InventoryComponent | null = null;
  private hoveredSlot: EquipmentSlot | null = null;
  private slotSize: number = 50;
  private padding: number = 10;

  // Double-click detection
  private lastClickTime: number = 0;
  private lastClickSlot: EquipmentSlot | null = null;
  private doubleClickThreshold: number = 300; // ms

  // Slot layout configuration
  private slotConfigs: SlotConfig[] = [
    { slot: 'head', label: 'Head', x: 1, y: 0 },
    { slot: 'chest', label: 'Chest', x: 1, y: 1 },
    { slot: 'hands', label: 'Hands', x: 0, y: 1 },
    { slot: 'legs', label: 'Legs', x: 1, y: 2 },
    { slot: 'feet', label: 'Feet', x: 1, y: 3 },
    { slot: 'accessory', label: 'Accessory', x: 2, y: 1 }
  ];

  // Callback for when item should go back to inventory
  public onUnequipItem: ((item: ArmorItem) => void) | null = null;

  constructor() {
    super(0, 0, 180, 300);
    this.isDraggable = true;
  }

  show(): void {
    super.show();
    this.title = t('equip_title');
  }

  setEquipment(equipment: EquipmentComponent): void {
    this.equipment = equipment;
  }

  setInventory(inventory: InventoryComponent): void {
    this.inventory = inventory;
  }

  protected onPanelClick(pos: Vector2): boolean {
    if (!this.equipment || !this.inventory) return true;

    const slot = this.getSlotAtPosition(pos);
    const now = Date.now();

    // Check for double-click on same slot
    if (slot && slot === this.lastClickSlot && (now - this.lastClickTime) < this.doubleClickThreshold) {
      const equippedItem = this.equipment.getSlot(slot);
      if (equippedItem) {
        // Check if inventory has space
        if (!this.inventory.isFull()) {
          // Unequip and add back to inventory
          const unequipped = this.equipment.unequip(slot);
          if (unequipped) {
            if (this.onUnequipItem) {
              this.onUnequipItem(unequipped);
            } else {
              // Default: add back to inventory
              this.inventory.addItem(unequipped, 1);
            }
          }
        }
        this.lastClickTime = 0;
        this.lastClickSlot = null;
        return true;
      }
    }

    // Update last click tracking
    this.lastClickTime = now;
    this.lastClickSlot = slot;

    return true;
  }

  handleMouseMove(pos: Vector2): boolean {
    const panelDragging = super.handleMouseMove(pos);

    // Always update hovered slot for tooltip (even during panel drag)
    if (this.isVisible && this.containsPoint(pos)) {
      this.hoveredSlot = this.getSlotAtPosition(pos);
    } else {
      this.hoveredSlot = null;
    }

    return panelDragging || this.containsPoint(pos);
  }

  private getSlotAtPosition(pos: Vector2): EquipmentSlot | null {
    const contentX = this.bounds.x + this.padding;
    const contentY = this.bounds.y + this.titleBarHeight + this.padding;

    for (const config of this.slotConfigs) {
      const slotX = contentX + config.x * (this.slotSize + 5);
      const slotY = contentY + config.y * (this.slotSize + 5);

      if (pos.x >= slotX && pos.x <= slotX + this.slotSize &&
          pos.y >= slotY && pos.y <= slotY + this.slotSize) {
        return config.slot;
      }
    }

    return null;
  }

  protected renderContent(ctx: CanvasRenderingContext2D): void {
    if (!this.equipment) {
      ctx.fillStyle = '#888';
      ctx.font = '14px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(t('equip_no_equipment'), this.bounds.x + this.bounds.width / 2, this.bounds.y + 100);
      return;
    }

    const startX = this.bounds.x + this.padding;
    const startY = this.bounds.y + this.titleBarHeight + this.padding;

    // Draw equipment slots
    for (const config of this.slotConfigs) {
      const x = startX + config.x * (this.slotSize + 5);
      const y = startY + config.y * (this.slotSize + 5);
      const item = this.equipment.getSlot(config.slot);
      const isHovered = config.slot === this.hoveredSlot;

      // Slot background
      if (isHovered) {
        ctx.fillStyle = 'rgba(78, 204, 163, 0.3)';
      } else {
        ctx.fillStyle = 'rgba(50, 50, 70, 0.8)';
      }
      ctx.fillRect(x, y, this.slotSize, this.slotSize);

      // Border with slot-specific color
      const slotColor = this.getSlotColor(config.slot);
      ctx.strokeStyle = isHovered ? '#7dffcc' : slotColor;
      ctx.lineWidth = isHovered ? 2 : 1;
      ctx.strokeRect(x, y, this.slotSize, this.slotSize);

      // Slot icon/label if empty
      if (!item) {
        ctx.fillStyle = '#444';
        ctx.font = '10px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(this.getSlotLabel(config.slot), x + this.slotSize / 2, y + this.slotSize / 2 + 4);
      } else {
        // Render equipped item
        this.renderEquippedItem(ctx, x, y, item);
      }
    }

    // Draw total stats at bottom
    this.renderStatsSummary(ctx, startX, startY + 4 * (this.slotSize + 5) + 10);

    // Render tooltip for hovered slot
    if (this.hoveredSlot) {
      const item = this.equipment.getSlot(this.hoveredSlot);
      if (item) {
        this.renderTooltip(ctx, item);
      }
    }
  }

  private getSlotColor(slot: EquipmentSlot): string {
    switch (slot) {
      case 'head': return '#6a9ec9';
      case 'chest': return '#c96a6a';
      case 'legs': return '#6ac98a';
      case 'feet': return '#c9c26a';
      case 'hands': return '#9a6ac9';
      case 'accessory': return '#c99a6a';
      default: return '#555';
    }
  }

  private getSlotLabel(slot: EquipmentSlot): string {
    switch (slot) {
      case 'head': return t('equip_slot_head');
      case 'chest': return t('equip_slot_chest');
      case 'hands': return t('equip_slot_hands');
      case 'legs': return t('equip_slot_legs');
      case 'feet': return t('equip_slot_feet');
      case 'accessory': return t('equip_slot_accessory');
      default: return slot;
    }
  }

  private renderEquippedItem(ctx: CanvasRenderingContext2D, x: number, y: number, item: ArmorItem): void {
    const iconSize = this.slotSize - 10;
    const iconX = x + 5;
    const iconY = y + 5;

    // Try to render pixel art icon first
    if (hasItemIcon(item.id)) {
      renderItemIcon(ctx, item.id, iconX, iconY, iconSize);
    } else {
      // Fallback to colored square
      ctx.fillStyle = item.getColor();
      ctx.fillRect(iconX, iconY, iconSize, iconSize);

      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1;
      ctx.strokeRect(iconX, iconY, iconSize, iconSize);

      // Item initial
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 14px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(item.name.charAt(0), x + this.slotSize / 2, y + this.slotSize / 2 + 5);
    }

    // Durability bar
    const durPercent = item.getDurabilityPercent();
    if (durPercent < 1) {
      const barWidth = this.slotSize - 4;
      const barHeight = 3;
      const barX = x + 2;
      const barY = y + this.slotSize - 5;

      ctx.fillStyle = '#333';
      ctx.fillRect(barX, barY, barWidth, barHeight);

      ctx.fillStyle = durPercent > 0.3 ? '#4ecca3' : '#e74c3c';
      ctx.fillRect(barX, barY, barWidth * durPercent, barHeight);
    }
  }

  private renderStatsSummary(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    if (!this.equipment) return;

    const bonuses = this.equipment.getTotalBonuses();

    ctx.fillStyle = '#aaa';
    ctx.font = '11px monospace';
    ctx.textAlign = 'left';

    let lineY = y;
    const lineHeight = 14;

    // Defense
    ctx.fillStyle = '#4682b4';
    ctx.fillText(`${t('equip_stat_def')} ${bonuses.defense}`, x, lineY);
    lineY += lineHeight;

    // Show non-zero bonuses
    if (bonuses.maxHealth && bonuses.maxHealth > 0) {
      ctx.fillStyle = '#e74c3c';
      ctx.fillText(`+${bonuses.maxHealth} ${t('equip_stat_hp')}`, x, lineY);
      lineY += lineHeight;
    }

    if (bonuses.moveSpeed && bonuses.moveSpeed > 0) {
      ctx.fillStyle = '#3498db';
      ctx.fillText(`+${Math.round(bonuses.moveSpeed * 100)}% ${t('equip_stat_speed')}`, x, lineY);
      lineY += lineHeight;
    }

    if (bonuses.attackDamage && bonuses.attackDamage > 0) {
      ctx.fillStyle = '#e67e22';
      ctx.fillText(`+${bonuses.attackDamage} ${t('equip_stat_atk')}`, x, lineY);
    }
  }

  private renderTooltip(ctx: CanvasRenderingContext2D, item: ArmorItem): void {
    const tooltipX = this.bounds.x + this.bounds.width + 10;
    const tooltipY = this.bounds.y + this.titleBarHeight;
    const tooltipWidth = 160;
    const tooltipHeight = 100;

    // Background
    ctx.fillStyle = 'rgba(20, 20, 40, 0.95)';
    ctx.fillRect(tooltipX, tooltipY, tooltipWidth, tooltipHeight);

    ctx.strokeStyle = '#4682b4';
    ctx.lineWidth = 1;
    ctx.strokeRect(tooltipX, tooltipY, tooltipWidth, tooltipHeight);

    // Get localized item info
    const itemInfo = Localization.getItem(item.id);
    const itemName = itemInfo?.name || item.name;

    // Item name
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 12px monospace';
    ctx.textAlign = 'left';
    ctx.fillText(itemName, tooltipX + 10, tooltipY + 18);

    // Slot type
    ctx.fillStyle = this.getSlotColor(item.slot);
    ctx.font = '10px monospace';
    ctx.fillText(this.getSlotLabel(item.slot).toUpperCase(), tooltipX + 10, tooltipY + 32);

    // Defense
    ctx.fillStyle = '#4682b4';
    ctx.fillText(`${t('equip_tooltip_defense')} ${item.getTotalDefense()}`, tooltipX + 10, tooltipY + 48);

    // Bonuses
    let bonusY = 62;
    if (item.bonuses.maxHealth) {
      ctx.fillStyle = '#e74c3c';
      ctx.fillText(`+${item.bonuses.maxHealth} ${t('equip_tooltip_max_hp')}`, tooltipX + 10, tooltipY + bonusY);
      bonusY += 14;
    }
    if (item.bonuses.moveSpeed) {
      ctx.fillStyle = '#3498db';
      ctx.fillText(`+${Math.round(item.bonuses.moveSpeed * 100)}% ${t('equip_stat_speed')}`, tooltipX + 10, tooltipY + bonusY);
      bonusY += 14;
    }

    // Click hint
    ctx.fillStyle = '#666';
    ctx.font = '9px monospace';
    ctx.fillText(t('equip_hint_unequip'), tooltipX + 10, tooltipY + tooltipHeight - 8);
  }
}
